/**
 * Dashboard Global State
 * Zustand store for managing dashboard state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export interface Metric {
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  averageLatency: number;
  tokensSaved: number;
  costSaved: number;
  secretsMasked?: number;
  activeAgents: number;
  totalAgents: number;
  uptime: number;
  timestamp: string;
  tier?: 'free' | 'pro';
}

export interface Agent {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'error';
  capabilities: string[];
  tasksCompleted: number;
  lastSeen: string;
  errorCount: number;
  cacheHitRate: number;
}

export interface DashboardEvent {
  id: string;
  timestamp: string;
  type: 
    | 'cache_hit' 
    | 'cache_miss' 
    | 'agent_join' 
    | 'agent_leave' 
    | 'error'
    | 'mcp_tool_call'
    | 'brain_hit'
    | 'brain_miss'
    | 'contextSqueeze'
    | 'complexityRouting'
    | 'historyPrune'
    | 'clipboard';
  agentId?: string;
  queryHash?: string;
  latency?: number;
  tokensSaved?: number;
  costSaved?: number;
  tool?: string;
  args?: any;
  result?: string;
  tokensImpact?: number;
  error?: string | null;
  metadata?: any;
}

export interface TopQuery {
  queryHash: string;
  query: string;
  hitCount: number;
  totalTokens: number;
  costSaved: number;
  lastHit: string;
}

export interface NeuralSearchResult {
  id: string;
  prompt: string;
  response: any;
  timestamp: string;
  similarity: number;
}

export interface SavingsSourceStats {
  events: number;
  tokensSaved: number;
  costSaved: number;
}

export interface SavingsBreakdown {
  clipboard: SavingsSourceStats;
  contextSqueeze: SavingsSourceStats;
  historyPrune: SavingsSourceStats;
  complexityRouting: SavingsSourceStats;
  cache: SavingsSourceStats;
  total: SavingsSourceStats;
  sessionStartedAt: string;
}

export interface DashboardState {
  // Metrics
  metrics: Metric | null;
  isLoadingMetrics: boolean;
  metricsError: string | null;

  // Agents
  agents: Agent[];
  selectedAgentId: string | null;
  isLoadingAgents: boolean;

  // Events
  events: DashboardEvent[];
  eventFilter: 'all' | 'hits' | 'misses' | 'joins' | 'leaves' | 'errors';
  eventPage: number;
  eventPageSize: number;
  totalEvents: number;

  // Cache Stats
  topQueries: TopQuery[];
  isLoadingCacheStats: boolean;

  // WebSocket Connection
  isConnected: boolean;
  connectionStatus: 'idle' | 'connecting' | 'connected' | 'disconnected';

  // Debugger
  isPaused: boolean;
  selectedEventId: string | null;
  debuggerState: any | null;

  // UI State
  showDebugger: boolean;
  activeSidePanel: 'agents' | 'stats' | 'timeline' | 'brain' | 'tools';
  refreshIntervalSeconds: number;

  // Search
  searchQuery: string;
  searchResults: NeuralSearchResult[];
  isSearching: boolean;

  // Savings Breakdown
  savingsBreakdown: SavingsBreakdown | null;

  // Actions
  setMetrics: (metrics: Metric) => void;
  setMetricsLoading: (loading: boolean) => void;
  setMetricsError: (error: string | null) => void;

  setAgents: (agents: Agent[]) => void;
  setSelectedAgent: (agentId: string | null) => void;
  addAgent: (agent: Agent) => void;
  removeAgent: (agentId: string) => void;

  setEvents: (events: DashboardEvent[]) => void;
  addEvent: (event: DashboardEvent) => void;
  setEventFilter: (filter: DashboardEvent['type'] | 'all') => void;
  setEventPage: (page: number) => void;
  setTotalEvents: (total: number) => void;

  setTopQueries: (queries: TopQuery[]) => void;

  setConnected: (connected: boolean) => void;
  setConnectionStatus: (status: 'idle' | 'connecting' | 'connected' | 'disconnected') => void;

  setPaused: (paused: boolean) => void;
  setSelectedEvent: (eventId: string | null) => void;
  setDebuggerState: (state: any) => void;

  setShowDebugger: (show: boolean) => void;
  setActiveSidePanel: (panel: 'agents' | 'stats' | 'timeline' | 'brain' | 'tools') => void;
  setRefreshIntervalSeconds: (seconds: number) => void;

  setSearchQuery: (query: string) => void;
  setSearchResults: (results: NeuralSearchResult[]) => void;
  setIsSearching: (isSearching: boolean) => void;

  setSavingsBreakdown: (breakdown: SavingsBreakdown) => void;

  reset: () => void;
}

const getInitialRefreshIntervalSeconds = () => {
  if (typeof window === 'undefined') {
    return 5;
  }

  try {
    const saved = window.localStorage.getItem('dashboardSettings');
    if (!saved) {
      return 5;
    }

    const parsed = JSON.parse(saved);
    const value = Number(parsed.refreshIntervalSeconds ?? parsed.refreshInterval ?? 5);
    return Number.isFinite(value) ? Math.min(30, Math.max(2, value)) : 5;
  } catch {
    return 5;
  }
};

const initialState = {
  metrics: null,
  isLoadingMetrics: false,
  metricsError: null,

  agents: [],
  selectedAgentId: null,
  isLoadingAgents: false,

  events: [],
  eventFilter: 'all' as const,
  eventPage: 1,
  eventPageSize: 50,
  totalEvents: 0,

  topQueries: [],
  isLoadingCacheStats: false,

  isConnected: false,
  connectionStatus: 'idle' as const,

  isPaused: false,
  selectedEventId: null,
  debuggerState: null,

  showDebugger: false,
  activeSidePanel: 'agents' as const,
  refreshIntervalSeconds: getInitialRefreshIntervalSeconds(),

  searchQuery: '',
  searchResults: [],
  isSearching: false,

  savingsBreakdown: null,
};

export const useDashboardStore = create<DashboardState>()(
  immer((set) => ({
    ...initialState,

    setMetrics: (metrics: Metric) => {
      set((state) => {
        state.metrics = metrics;
        state.isLoadingMetrics = false;
        state.metricsError = null;
      });
    },

    setMetricsLoading: (loading: boolean) => {
      set((state) => {
        state.isLoadingMetrics = loading;
      });
    },

    setMetricsError: (error: string | null) => {
      set((state) => {
        state.metricsError = error;
      });
    },

    setAgents: (agents: Agent[]) => {
      set((state) => {
        state.agents = agents;
        state.isLoadingAgents = false;
      });
    },

    setSelectedAgent: (agentId: string | null) => {
      set((state) => {
        state.selectedAgentId = agentId;
      });
    },

    addAgent: (agent: Agent) => {
      set((state) => {
        const existing = state.agents.find((a) => a.id === agent.id);
        if (!existing) {
          state.agents.push(agent);
        }
      });
    },

    removeAgent: (agentId: string) => {
      set((state) => {
        state.agents = state.agents.filter((a) => a.id !== agentId);
        if (state.selectedAgentId === agentId) {
          state.selectedAgentId = null;
        }
      });
    },

    setEvents: (events: DashboardEvent[]) => {
      set((state) => {
        state.events = events;
      });
    },

    addEvent: (event: DashboardEvent) => {
      set((state) => {
        state.events.unshift(event); // Add to beginning for newest-first
        if (state.events.length > 1000) {
          state.events.pop();
        }
      });
    },

    setEventFilter: (filter: DashboardEvent['type'] | 'all') => {
      set((state) => {
        state.eventFilter = filter === 'all' ? 'all' : (filter as any);
        state.eventPage = 1;
      });
    },

    setEventPage: (page: number) => {
      set((state) => {
        state.eventPage = page;
      });
    },

    setTotalEvents: (total: number) => {
      set((state) => {
        state.totalEvents = total;
      });
    },

    setTopQueries: (queries: TopQuery[]) => {
      set((state) => {
        state.topQueries = queries;
        state.isLoadingCacheStats = false;
      });
    },

    setConnected: (connected: boolean) => {
      set((state) => {
        state.isConnected = connected;
      });
    },

    setConnectionStatus: (status: 'idle' | 'connecting' | 'connected' | 'disconnected') => {
      set((state) => {
        state.connectionStatus = status;
      });
    },

    setPaused: (paused: boolean) => {
      set((state) => {
        state.isPaused = paused;
      });
    },

    setSelectedEvent: (eventId: string | null) => {
      set((state) => {
        state.selectedEventId = eventId;
      });
    },

    setDebuggerState: (state: any) => {
      set((dashboardState) => {
        dashboardState.debuggerState = state;
      });
    },

    setShowDebugger: (show: boolean) => {
      set((state) => {
        state.showDebugger = show;
      });
    },

    setActiveSidePanel: (panel: 'agents' | 'stats' | 'timeline' | 'brain' | 'tools') => {
      set((state) => {
        state.activeSidePanel = panel;
      });
    },

    setRefreshIntervalSeconds: (seconds: number) => {
      set((state) => {
        state.refreshIntervalSeconds = Math.min(30, Math.max(2, seconds));
      });
    },

    setSearchQuery: (query: string) => {
      set((state) => {
        state.searchQuery = query;
      });
    },

    setSearchResults: (results: NeuralSearchResult[]) => {
      set((state) => {
        state.searchResults = results;
        state.isSearching = false;
      });
    },

    setIsSearching: (isSearching: boolean) => {
      set((state) => {
        state.isSearching = isSearching;
      });
    },

    setSavingsBreakdown: (breakdown: SavingsBreakdown) => {
      set((state) => {
        state.savingsBreakdown = breakdown;
      });
    },

    reset: () => {
      set(() => initialState);
    },
  }))
);
